import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactImportModal = ({ isOpen, onClose, onImport }) => {
  const [importType, setImportType] = useState('csv');
  const [file, setFile] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({
    name: '',
    email: '',
    outlet: '',
    phone: '',
    beats: '',
    location: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const importTypeOptions = [
  { value: 'csv', label: 'CSV File' },
  { value: 'excel', label: 'Excel File (.xlsx)' },
  { value: 'vcard', label: 'vCard (.vcf)' },
  { value: 'json', label: 'JSON File' }];


  const csvFields = [
  'Name', 'Email', 'Outlet', 'Phone', 'Beats', 'Location',
  'Twitter', 'LinkedIn', 'Notes', 'Status'];


  const fieldOptions = [
  { value: '', label: 'Skip this field' },
  ...csvFields?.map((field) => ({
    value: field?.toLowerCase()?.replace(' ', '_'),
    label: field
  }))];


  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    setFile(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsProcessing(true);

    // Simulate import process
    setTimeout(() => {
      const mockImportedContacts = [
      {
        id: Date.now() + 1,
        name: "Sarah Johnson",
        email: "sarah.johnson@techcrunch.com",
        outlet: "TechCrunch",
        outletType: "Online Publication",
        phone: "+1 (555) 123-4567",
        beats: ["Technology", "Startups"],
        location: "San Francisco",
        engagement: "High",
        successRate: 85,
        status: "Active",
        avatar: "https://images.unsplash.com/photo-1706565029071-f8f70ce91e71",
        avatarAlt: "Professional headshot of woman with brown hair in white blazer",
        twitter: "@sarahjtech",
        linkedin: "linkedin.com/in/sarahjohnson",
        notes: "Prefers email pitches on Tuesday mornings. Interested in AI and machine learning stories.",
        recentInteractions: [
        {
          type: "email",
          subject: "AI Startup Funding Round",
          date: "Oct 28, 2024",
          status: "Responded"
        }]

      }];


      onImport(mockImportedContacts);
      setIsProcessing(false);
      onClose();

      // Reset form
      setFile(null);
      setFieldMapping({
        name: '',
        email: '',
        outlet: '',
        phone: '',
        beats: '',
        location: ''
      });
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevated w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Import Contacts</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8">

            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Import Type Selection */}
          <div>
            <Select
              label="Import Format"
              description="Choose the format of your contact file"
              options={importTypeOptions}
              value={importType}
              onChange={setImportType} />

          </div>

          {/* File Upload */}
          <div>
            <Input
              type="file"
              label="Select File"
              description="Upload your contact file for import"
              accept={importType === 'csv' ? '.csv' : importType === 'excel' ? '.xlsx' : importType === 'vcard' ? '.vcf' : '.json'}
              onChange={handleFileChange} />

          </div>

          {/* Field Mapping */}
          {file && importType === 'csv' &&
          <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Field Mapping</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Map the columns in your CSV file to the contact fields in PenGame PR Hub.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                label="Name Column"
                options={fieldOptions}
                value={fieldMapping?.name}
                onChange={(value) => setFieldMapping((prev) => ({ ...prev, name: value }))}
                placeholder="Select column" />

                
                <Select
                label="Email Column"
                options={fieldOptions}
                value={fieldMapping?.email}
                onChange={(value) => setFieldMapping((prev) => ({ ...prev, email: value }))}
                placeholder="Select column" />

                
                <Select
                label="Outlet Column"
                options={fieldOptions}
                value={fieldMapping?.outlet}
                onChange={(value) => setFieldMapping((prev) => ({ ...prev, outlet: value }))}
                placeholder="Select column" />

                
                <Select
                label="Phone Column"
                options={fieldOptions}
                value={fieldMapping?.phone}
                onChange={(value) => setFieldMapping((prev) => ({ ...prev, phone: value }))}
                placeholder="Select column" />

                
                <Select
                label="Beats Column"
                options={fieldOptions}
                value={fieldMapping?.beats}
                onChange={(value) => setFieldMapping((prev) => ({ ...prev, beats: value }))}
                placeholder="Select column" />

                
                <Select
                label="Location Column"
                options={fieldOptions}
                value={fieldMapping?.location}
                onChange={(value) => setFieldMapping((prev) => ({ ...prev, location: value }))}
                placeholder="Select column" />

              </div>
            </div>
          }

          {/* Import Preview */}
          {file &&
          <div className="bg-muted/30 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="FileText" size={16} className="text-primary" />
                <span className="font-medium text-foreground">File Preview</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>File: {file?.name}</p>
                <p>Size: {(file?.size / 1024)?.toFixed(1)} KB</p>
                <p>Type: {file?.type || 'Unknown'}</p>
              </div>
            </div>
          }
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}>

            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleImport}
            disabled={!file || isProcessing}
            loading={isProcessing}
            iconName="Upload"
            iconPosition="left">

            {isProcessing ? 'Importing...' : 'Import Contacts'}
          </Button>
        </div>
      </div>
    </div>);

};

export default ContactImportModal;